import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Button,
  Card,
  Empty,
  Alert,
  Space,
  Spin,
  Tag,
  Typography,
  Select,
  message,
} from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import InstitutionAccountsTable from "../../../components/accounts/InstitutionAccountsTable";
import AccountTransactionModal from "../../../components/transaction/AccountTransactionModal";
import { getTypeColor } from "../../../utils/themeUtils";
import WalletCard from "../../../components/balances/WalletCard";
import { createInstitutionWalletColumns } from "../../../components/balances/institutionWalletColumns";
import { useInstitutionWallets } from "./hooks/useInstitutionWallets";
import { useInstitutionWalletDashboard } from "./hooks/useInstitutionWalletDashboard";
import InstitutionWalletWeeklyDashboard from "../../../components/dashboard/InstitutionWalletWeeklyDashboard";
import { hasAnyRole } from "../../../api/auth";

const { Title } = Typography;

export default function InstitutionWalletsPage() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currencyFromUrl = searchParams.get("currency");
  const walletRefs = useRef({});
  const [selectedBank, setSelectedBank] = useState("ALL");

  const canOpenManualModal = hasAnyRole(["ADMIN", "MODERATOR"]);

  const {
    institution,
    groupedByCurrency,
    selectedCurrency,
    selectedCurrencyGroup,
    loading,
    error,
    transactionModalOpen,
    transactionMode,
    selectedAccount,
    transactionSubmitting,
    manualCurrencyMatchedSenderAccounts,
    internalInstitutionAccounts,
    setSelectedCurrency,
    loadData,
    closeTransactionModal,
    openManualModal,
    openTransferModal,
    handleSubmitTransaction,
    manualSenderInstitutionId,
    externalInstitutions,
    externalInstitutionsLoading,
    handleTransferScopeChange,
  } = useInstitutionWallets(institutionId, navigate, currencyFromUrl);

  const { dashboardData, dashboardLoading, dashboardError, loadDashboard } =
    useInstitutionWalletDashboard(institutionId);

  const handleOpenManualModal = useCallback(
    (record) => {
      if (!canOpenManualModal) {
        message.error("Танд гараар гүйлгээ нээх эрх алга");
        return;
      }

      openManualModal(record);
    },
    [canOpenManualModal, openManualModal]
  );

  const handleOpenTransferModal = useCallback(
    (record) => {
      openTransferModal(record);
    },
    [openTransferModal]
  );

  const handleSelectCurrency = (currency) => {
    setSelectedCurrency(currency);
    setSelectedBank("ALL");

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (currency) {
        next.set("currency", currency);
      } else {
        next.delete("currency");
      }

      return next;
    });
  };

  const filteredAccountsSource = useMemo(() => {
    if (
      Array.isArray(selectedCurrencyGroup?.accounts) &&
      selectedCurrencyGroup.accounts.length > 0
    ) {
      return selectedCurrencyGroup.accounts;
    }

    return groupedByCurrency.flatMap((group) =>
      Array.isArray(group.accounts) ? group.accounts : []
    );
  }, [selectedCurrencyGroup, groupedByCurrency]);

  const bankOptions = useMemo(() => {
    return [
      ...new Set(
        filteredAccountsSource
          .map((account) => account?.bank)
          .filter((bank) => typeof bank === "string" && bank.trim() !== "")
      ),
    ];
  }, [filteredAccountsSource]);

  const filteredAccounts = useMemo(() => {
    const accounts = selectedCurrencyGroup?.accounts || [];

    if (selectedBank === "ALL") return accounts;

    return accounts.filter((account) => account.bank === selectedBank);
  }, [selectedCurrencyGroup, selectedBank]);

  useEffect(() => {
    if (!selectedCurrency) return;

    const node = walletRefs.current[selectedCurrency];
    if (!node) return;

    node.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedCurrency]);

  useEffect(() => {
    setSelectedBank("ALL");
  }, [selectedCurrency]);

  useEffect(() => {
    if (!groupedByCurrency?.length) return;
    if (selectedCurrency) return;

    const cny = groupedByCurrency.find((g) => g.currency === "CNY");

    if (cny) {
      setSelectedCurrency("CNY");
      return;
    }

    setSelectedCurrency(groupedByCurrency[0].currency);
  }, [groupedByCurrency, selectedCurrency, setSelectedCurrency]);

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!institution && !error) {
    return <Empty description="Байгууллага олдсонгүй" />;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto">

        {/* Page header — one cohesive row */}
        <div className="mb-6 flex items-center gap-3">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/manage/institutions")}
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Title level={4} className="!mb-0 truncate text-slate-900">
                {institution?.name || "Байгууллага"}
              </Title>
              <Tag color={getTypeColor(institution?.type || "UNKNOWN")} className="shrink-0">
                {institution?.type || "ТОДОРХОЙГҮЙ"}
              </Tag>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            className="shrink-0"
            onClick={async () => {
              await Promise.all([loadData(), loadDashboard()]);
            }}
          >
            Шинэчлэх
          </Button>
        </div>

        {error ? (
          <Card className="mb-6 rounded-2xl">
            <Alert
              type="error"
              message="Ачааллахад алдаа гарлаа"
              description={error}
              showIcon
            />
          </Card>
        ) : null}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_460px]">
          <div className="space-y-6">

            {/* Currency wallet cards */}
            <Card
              className="rounded-2xl shadow-sm"
              styles={{ body: { padding: "16px 20px" } }}
            >
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Валютын баланс
              </div>
              {groupedByCurrency.length === 0 ? (
                <Empty description="Энэ байгууллагад данс олдсонгүй" />
              ) : (
                <div className="overflow-x-auto overflow-y-hidden p-2">
                  <div className="flex min-w-max gap-3 pb-1">
                    {[...groupedByCurrency]
                      .sort((a, b) => {
                        if (a.currency === "CNY") return -1;
                        if (b.currency === "CNY") return 1;
                        return 0;
                      })
                      .map((group) => (
                        <div
                          key={group.currency}
                          ref={(node) => {
                            if (node) walletRefs.current[group.currency] = node;
                          }}
                          className="shrink-0"
                        >
                          <WalletCard
                            currency={group.currency}
                            walletBalance={group.walletBalance}
                            totalBalance={group.totalBalance}
                            accountCount={group.accounts.length}
                            active={selectedCurrency === group.currency}
                            onClick={() => handleSelectCurrency(group.currency)}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Accounts table — bank filter lives in the header via extra prop */}
            <InstitutionAccountsTable
              title={
                selectedCurrencyGroup
                  ? `${selectedCurrencyGroup.currency} данснууд`
                  : "Данснууд"
              }
              subtitle={
                selectedBank !== "ALL" ? `Банкаар шүүсэн: ${selectedBank}` : ""
              }
              extra={
                bankOptions.length > 0 ? (
                  <Select
                    value={selectedBank}
                    onChange={setSelectedBank}
                    className="w-[200px]"
                    placeholder="Банк сонгох"
                    options={[
                      { value: "ALL", label: "Бүх банк" },
                      ...bankOptions.map((bank) => ({ value: bank, label: bank })),
                    ]}
                  />
                ) : null
              }
              loading={loading}
              accounts={filteredAccounts}
              fetchData={async () => {
                await Promise.all([loadData(), loadDashboard()]);
              }}
              createColumns={({ bulkSelectMode }) =>
                createInstitutionWalletColumns({
                  onManual: handleOpenManualModal,
                  onTransfer: handleOpenTransferModal,
                  canOpenManualModal,
                  bulkSelectMode,
                })
              }
            />
          </div>

          <div>
            <div className="xl:sticky xl:top-6">
              <InstitutionWalletWeeklyDashboard
                institutionName={institution.name}
                institutionType={institution.type}
                institutionId={institutionId}
                data={dashboardData}
                selectedCurrency={selectedCurrency}
                loading={dashboardLoading}
                error={dashboardError}
              />
            </div>
          </div>
        </div>

        <AccountTransactionModal
          title={`${institution?.name}`}
          open={transactionModalOpen}
          mode={transactionMode}
          selectedAccount={selectedAccount}
          manualSenderInstitutionId={manualSenderInstitutionId}
          manualSenderAccounts={manualCurrencyMatchedSenderAccounts}
          institutionAccounts={internalInstitutionAccounts}
          externalInstitutions={externalInstitutions}
          externalInstitutionsLoading={externalInstitutionsLoading}
          onTransferScopeChange={handleTransferScopeChange}
          onCancel={closeTransactionModal}
          onSubmit={handleSubmitTransaction}
          loading={transactionSubmitting}
          loadDashboard={loadDashboard}
          loadData={loadData}
        />
      </div>
    </div>
  );
}